import { useState, useCallback } from 'react';
import type { SearchLocation } from '@/types';

export interface GeolocationError {
  code: 'PERMISSION_DENIED' | 'POSITION_UNAVAILABLE' | 'TIMEOUT' | 'NOT_SUPPORTED' | 'UNKNOWN';
  message: string;
}

interface UseGeolocationResult {
  location: SearchLocation | null;
  isLocating: boolean;
  error: GeolocationError | null;
  requestLocation: () => void;
  clearError: () => void;
}

const ERROR_MESSAGES: Record<GeolocationError['code'], string> = {
  PERMISSION_DENIED: 'Location access denied. Please enable location in your browser settings or search manually.',
  POSITION_UNAVAILABLE: 'Unable to determine your location. Please try searching manually.',
  TIMEOUT: 'Location request timed out. Please try again or search manually.',
  NOT_SUPPORTED: 'Geolocation is not supported by your browser. Please search manually.',
  UNKNOWN: 'An error occurred while getting your location. Please try searching manually.',
};

export function useGeolocation(): UseGeolocationResult {
  const [location, setLocation] = useState<SearchLocation | null>(null);
  const [isLocating, setIsLocating] = useState(false);
  const [error, setError] = useState<GeolocationError | null>(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const requestLocation = useCallback(() => {
    // Check if geolocation is supported
    if (!navigator.geolocation) {
      setError({
        code: 'NOT_SUPPORTED',
        message: ERROR_MESSAGES.NOT_SUPPORTED,
      });
      return;
    }

    setIsLocating(true);
    setError(null);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const newLocation: SearchLocation = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        };
        setLocation(newLocation);
        setIsLocating(false);
        setError(null);
      },
      (geoError) => {
        setIsLocating(false);

        let errorCode: GeolocationError['code'];
        switch (geoError.code) {
          case geoError.PERMISSION_DENIED:
            errorCode = 'PERMISSION_DENIED';
            break;
          case geoError.POSITION_UNAVAILABLE:
            errorCode = 'POSITION_UNAVAILABLE';
            break;
          case geoError.TIMEOUT:
            errorCode = 'TIMEOUT';
            break;
          default:
            errorCode = 'UNKNOWN';
        }

        setError({
          code: errorCode,
          message: ERROR_MESSAGES[errorCode],
        });
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000, // 5 minutes
      }
    );
  }, []);

  return {
    location,
    isLocating,
    error,
    requestLocation,
    clearError,
  };
}
